import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, Subscription } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig, TableEvent } from 'src/app/models/custom-table/custom-table.model';
import { SiteGroupDTO } from 'src/app/models/Sites/siteGroupDTO';
import { SnackbarType } from 'src/app/models/snackbar/snackbar.model';
import { SiteGroupsService } from 'src/app/shared/services/site-groups/site-groups.service';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { TableConfigService } from 'src/app/shared/services/table-config/table-config.service';

const USAGE_CODE = "SELECT_SITE_GROUP";

@Component({
  selector: 'app-select-site-groups-dialog',
  templateUrl: './select-site-groups-dialog.component.html',
  styleUrls: ['./select-site-groups-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectSiteGroupsDialogComponent implements OnInit, OnDestroy {
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public totalSiteGroups$ = new BehaviorSubject<number>(0);

  public siteGroupsList: SiteGroupDTO[];

  private siteGroupsSubscription: Subscription;

  public selectedRows: Object[] = [];

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  tableConfig: TableConfig[] = [
    {
          header: "CustomGrid.Options",
          field: FieldType.OPTIONS,
          fieldType: FieldType.OPTIONS,
        },
        {
          header: "Entity.token",
          field: "token",
          fieldType: FieldType.STRING,
          hidden: true,
          identifier: true,
        },
        {
          header: "Tax.code",
          field: "code",
          fieldType: FieldType.STRING,
        },
        {
          header: "Tax.description",
          field: "description",
          fieldType: FieldType.STRING,
          searchable: true,
        },
        {
          header: "Tax.enabled",
          field: "enabled",
          fieldType: FieldType.BOOLEAN,
        },
        {
          header: "Tax.createdOn",
          field: "createdOn",
          fieldType: FieldType.DATE_TIME,
          formatType: FormatType.DATETIME
        },
        {
          header: "Tax.updatedOn",
          field: "updatedOn",
          fieldType: FieldType.DATE_TIME,
          formatType: FormatType.DATETIME
        },
  ];

  constructor(
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private siteGroupsService: SiteGroupsService,
    private tableConfigService: TableConfigService,
    private dialogRef: MatDialogRef<SelectSiteGroupsDialogComponent>,
  ) { }

  ngOnInit(): void {
    this.columnInfo$ = this.getTableConfig();
    this.getSiteGroupsList();
  }

  getSiteGroupsList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    this.siteGroupsSubscription = this.siteGroupsService
      .getSiteGroupsList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalSiteGroups$.next(size);
          return items;
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("SiteGroup.siteGroupsLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      ).subscribe((result: SiteGroupDTO[]) => this.siteGroupsList = result);
  }

  getTableConfig() {
    this.isLoading$.next(true);
    return this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  onSelectedRow(event) {
    this.selectedRows = event;
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveSuccess"),
            type: SnackbarType.SUCCESS,
          })
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveError"),
            type: SnackbarType.SUCCESS,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getSiteGroupsList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onConfirmClick() {
    this.dialogRef.close(this.selectedRows);
  }

  isSelectedRowEmpty(): boolean {
    return this.selectedRows.length == 0;
  }

  ngOnDestroy(): void {
    if(this.siteGroupsSubscription) {
      this.siteGroupsSubscription.unsubscribe();
    };
  }
}
